import { z } from '@hono/zod-openapi'
import { katakanaZ } from '../lib/validation_lib'
import * as fileType from './file_type'

// id
export const idZ = z.number().openapi({ description: '賃借人ID', example: 1 })
export type id = z.infer<typeof idZ>

// roomId
export const roomIdZ = z
  .number()
  .openapi({ description: '入居先の部屋ID', example: 201 })
export type roomId = z.infer<typeof roomIdZ>

export const tenantApplicationIdZ = z.number().nullable().openapi({
  description: '入居申請ID',
  example: 1,
})
export type tenantApplicationId = z.infer<typeof tenantApplicationIdZ>

// name: 氏名
export const nameZ = z
  .string()
  .openapi({ description: '賃借人の氏名', example: '山田太郎' })
export type name = z.infer<typeof nameZ>

// name: 氏名カナ
export const nameKanaZ = katakanaZ.openapi({
  description: '賃借人の氏名カナ',
  example: 'ヤマダタロウ',
})
export type nameKana = z.infer<typeof nameKanaZ>

// phone1: 電話番号1（市外局番）
export const phone1Z = z
  .string()
  .min(1)
  .max(4)
  .regex(/^\d{1,4}$/)
  .openapi({ description: '電話番号の市外局番', example: '03' })
export type phone1 = z.infer<typeof phone1Z>

// phone2: 電話番号2（市内局番）
export const phone2Z = z
  .string()
  .min(1)
  .max(4)
  .regex(/^\d{1,4}$/)
  .openapi({ description: '電話番号の市内局番', example: '1234' })
export type phone2 = z.infer<typeof phone2Z>

// phone3: 電話番号3（加入者番号）
export const phone3Z = z
  .string()
  .min(1)
  .max(4)
  .regex(/^\d{1,4}$/)
  .openapi({ description: '電話番号の加入者番号', example: '5678' })
export type phone3 = z.infer<typeof phone3Z>

const optionalPhonePartZ = (max: number, example: string) =>
  z.string().max(max).regex(/^\d*$/).openapi({ example })

// phone4-6: optional second phone number
export const phone4Z = optionalPhonePartZ(4, '03')
export type phone4 = z.infer<typeof phone4Z>

export const phone5Z = optionalPhonePartZ(4, '1234')
export type phone5 = z.infer<typeof phone5Z>

export const phone6Z = optionalPhonePartZ(4, '5678')
export type phone6 = z.infer<typeof phone6Z>

// email: メールアドレス
export const emailZ = z.string().openapi({
  description: '賃借人のメールアドレス',
  example: 'taro@example.com',
})
export type email = z.infer<typeof emailZ>

// birthday: 生年月日
export const birthdayZ = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .nullable()
  .openapi({ description: '生年月日（YYYY-MM-DD）', example: '1990-01-01' })
export type birthday = z.infer<typeof birthdayZ>

// startDate: 契約開始日
export const startDateZ = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .openapi({
    description: '賃貸借契約の開始日（YYYY-MM-DD）',
    example: '2024-01-01',
  })
export type startDate = z.infer<typeof startDateZ>

// endDate: 契約終了日
export const endDateZ = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .nullable()
  .openapi({
    description: '賃貸借契約の終了日（YYYY-MM-DD）。null は契約中',
    example: '2025-01-01',
  })
export type endDate = z.infer<typeof endDateZ>

// endReason: 契約終了理由
export const TENANT_END_REASON_TENANT_MOVE_OUT = 'tenant_move_out'
export const TENANT_END_REASON_OWNER_CHANGE = 'owner_change'
export const TENANT_END_REASON_VALUES = [
  TENANT_END_REASON_TENANT_MOVE_OUT,
  TENANT_END_REASON_OWNER_CHANGE,
] as const
export const TENANT_END_REASONS = [
  { key: TENANT_END_REASON_TENANT_MOVE_OUT, value: '退去（入居者都合）' },
  { key: TENANT_END_REASON_OWNER_CHANGE, value: '所有者変更' },
] as const

export const endReasonZ = z.enum(TENANT_END_REASON_VALUES).nullable().openapi({
  description:
    '契約終了理由（tenant_move_out:退去(入居者都合) / owner_change:所有者変更）',
  example: null,
})
export type endReason = z.infer<typeof endReasonZ>

// rent: 家賃
export const rentZ = z
  .number()
  .openapi({ description: '家賃（円・月額）', example: 80000 })
export type rent = z.infer<typeof rentZ>

// commonAreaFee: 共益費
export const commonAreaFeeZ = z
  .number()
  .openapi({ description: '共益費（円・月額）', example: 5000 })
export type commonAreaFee = z.infer<typeof commonAreaFeeZ>

// isParking: 駐車場の有無
export const isParkingZ = z
  .boolean()
  .openapi({ description: '駐車場契約の有無', example: true })
export type isParking = z.infer<typeof isParkingZ>

// parkingFee: 駐車場料金
export const parkingFeeZ = z
  .number()
  .openapi({ description: '駐車場料金（円・月額）', example: 10000 })
export type parkingFee = z.infer<typeof parkingFeeZ>

// parkingNumber: 駐車場番号
export const parkingNumberZ = z
  .string()
  .openapi({ description: '駐車場の区画番号', example: 'No.12' })
export type parkingNumber = z.infer<typeof parkingNumberZ>

// otherFees: その他費用
export const otherFeesZ = z
  .array(
    z.object({
      label: z.string(),
      price: z.number(),
      tax: z.number(),
    }),
  )
  .nullable()
  .openapi({
    description:
      'その他費用の内訳（label:名目 / price:税抜金額(円) / tax:消費税額(円)）',
    example: [
      { label: '水道代', price: 2727, tax: 273 },
      { label: 'インターネット代', price: 1818, tax: 182 },
    ],
  })
export type otherFees = z.infer<typeof otherFeesZ>

// purpose: 用途
export const TENANT_PURPOSE_LIVING = '1'
export const TENANT_PURPOSE_BUSINESS = '2'
export const TENANT_PURPOSES = [
  { key: TENANT_PURPOSE_LIVING, value: '居住用' },
  { key: TENANT_PURPOSE_BUSINESS, value: '事業用' },
] as const
export const purposeZ = z
  .enum([TENANT_PURPOSE_LIVING, TENANT_PURPOSE_BUSINESS])
  .openapi({
    description: '用途（1:居住用 / 2:事業用）',
    example: TENANT_PURPOSE_LIVING,
  })
export type purpose = z.infer<typeof purposeZ>

// adminMemo: 管理者メモ
export const adminMemoZ = z
  .string()
  .openapi({ description: '管理者用メモ（社内用）', example: '要連絡' })
export type adminMemo = z.infer<typeof adminMemoZ>

// createdAt: 作成日時
export const createdAtStringZ = z.string().openapi({
  description: '作成日時（ISO8601）',
  example: '2024-01-01T00:00:00.000Z',
})
export type createdAtString = z.infer<typeof createdAtStringZ>

export const createdAtDateZ = z
  .date()
  .openapi({ example: '2024-01-01T00:00:00.000Z' })
export type createdAtDate = z.infer<typeof createdAtDateZ>

// updatedAt: 更新日時
export const updatedAtStringZ = z.string().openapi({
  description: '更新日時（ISO8601）',
  example: '2024-01-01T00:00:00.000Z',
})
export type updatedAtString = z.infer<typeof updatedAtStringZ>

export const updatedAtDateZ = z
  .date()
  .openapi({ example: '2024-01-01T00:00:00.000Z' })
export type updatedAtDate = z.infer<typeof updatedAtDateZ>

// deletedAt: 削除日時
export const deletedAtStringZ = z
  .string()
  .nullable()
  .openapi({ description: '削除日時（ISO8601）。null は未削除', example: null })
export type deletedAtString = z.infer<typeof deletedAtStringZ>

export const deletedAtDateZ = z.date().nullable().openapi({ example: null })
export type deletedAtDate = z.infer<typeof deletedAtDateZ>

// ownerDeletedAt: オーナー削除日時
export const ownerDeletedAtStringZ = z
  .string()
  .nullable()
  .openapi({ example: null })
export type ownerDeletedAtString = z.infer<typeof ownerDeletedAtStringZ>

export const ownerDeletedAtDateZ = z
  .date()
  .nullable()
  .openapi({ example: null })
export type ownerDeletedAtDate = z.infer<typeof ownerDeletedAtDateZ>

// echoesTenantId（Echoesの入居者ID）
export const echoesTenantIdZ = z.string().nullable().openapi({ example: '1' })
export type echoesTenantId = z.infer<typeof echoesTenantIdZ>

// echoesUpdatedAt（取り込み済み Echoes レコードの updated_at。等値比較で洗替要否を判定する）
export const echoesUpdatedAtDateZ = z
  .date()
  .nullable()
  .openapi({ example: null })
export type echoesUpdatedAtDate = z.infer<typeof echoesUpdatedAtDateZ>

// echoesFailedAt（Echoesからの同期に最後に失敗した日時。成功時は null に戻す）
export const echoesFailedAtDateZ = z
  .date()
  .nullable()
  .openapi({ example: null })
export type echoesFailedAtDate = z.infer<typeof echoesFailedAtDateZ>

// ファイル
export const uploadFilesZ = z
  .array(
    z.object({
      path: z.string().openapi({ example: 'tmp/uuid/timestamp' }),
      fileName: z.string().openapi({ example: 'memo-image.png' }),
      title: z.string().optional().openapi({ example: 'マイドキュメント' }),
      tenantFileType: fileType.tenantFileTypeZ,
    }),
  )
  .optional()
  .openapi({ example: [{}] })
export type uploadFiles = z.infer<typeof uploadFilesZ>

export const deleteFileIdsZ = z
  .array(z.number().int())
  .optional()
  .openapi({ example: [1, 2, 3] })
export type deleteFileIds = z.infer<typeof deleteFileIdsZ>
